// Turn a parsed Action proto (v2 or v1-ish) into plain-English bullets
// suitable for a CoT display column. No icons / colors here — the
// renderer styles. Output strings are mono-friendly (no smart quotes).

import { facingLabel, toolLabel } from "./tools.js";

export interface Hex2D {
  q: number;
  r: number;
}

export interface WeaponShot {
  tool: number;
  target_hex?: Hex2D;
  called_zone?: number;
}

export interface ActionLike {
  movement_tool?: number;
  movement_target?: Hex2D;
  movement_facing?: number;
  // v2 schema: array of weapon shots.
  weapons?: WeaponShot[];
  // v1 schema: a single primary shot.
  primary_tool?: number;
  primary_target?: Hex2D;
  reactive_tool?: number;
  called_shot_zone?: number;
  signal?: number;
}

function hex(h?: Hex2D | null): string {
  if (!h) return "(?, ?)";
  return `(${h.q}, ${h.r})`;
}

/**
 * `["DASH → (0,-2) face SE", "Fire MEDIUM_LASER at (6,0)", "React: POP_SMOKE"]`
 * Empty array means "hold position, no shots, no reactive".
 */
export function actionToProse(action: ActionLike | null | undefined): string[] {
  if (!action) return [];
  const out: string[] = [];

  const moveTool = action.movement_tool ?? 0;
  if (moveTool > 0) {
    const parts = [toolLabel(moveTool), "→", hex(action.movement_target)];
    if (action.movement_facing != null) parts.push(`face ${facingLabel(action.movement_facing)}`);
    out.push(parts.join(" "));
  } else if (action.movement_facing != null) {
    // No movement tool — stand-still facing pivot. Movement target is
    // optional here; some emitters omit it entirely when the mech
    // doesn't move.
    out.push(`HOLD face ${facingLabel(action.movement_facing)}`);
  }

  // v2 array form.
  if (Array.isArray(action.weapons) && action.weapons.length > 0) {
    for (const shot of action.weapons) {
      out.push(`Fire ${toolLabel(shot.tool)} at ${hex(shot.target_hex)}`);
    }
  } else if (action.primary_tool && action.primary_tool > 0) {
    // v1 single-shot form.
    out.push(`Fire ${toolLabel(action.primary_tool)} at ${hex(action.primary_target)}`);
  }

  if (action.reactive_tool && action.reactive_tool > 0) {
    out.push(`React: ${toolLabel(action.reactive_tool)}`);
  }

  if (action.called_shot_zone && action.called_shot_zone > 0) {
    out.push(`Called shot: zone ${action.called_shot_zone}`);
  }

  return out;
}
